import { Button, Card, DatePicker, Form, Grid, Input, Message, Modal, Select, Space } from "@arco-design/web-react";
import { InputCurrency, InputNumber } from "@/components/Numeric";
import CustomerSelect from "@/components/CustomerSelect";
import Address from "@/components/Address";
import { EnumSelect } from "@/components/Enum";
import { TermsDef } from "@/enums/common";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerAPI, SOAPI, InvoiceAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import _ from "lodash";
import Decimal from "decimal.js";
import { today } from "@/utils";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const TextArea = Input.TextArea;

export const SOAdd = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [shipAddresses, setShipAddresses] = useState([]);

    useEffect(() => {
        const fetchItems = async () => {
            let values = await ItemAPI.actives();
            values = _.sortBy(values, "itemName");
            setItems(values);
        };
        fetchItems();
    }, []);

    useEffect(() => {
        const initForm = async () => {
            form.setFieldsValue({
                soDate: today(),
            });
        };
        initForm();
    }, [form]);

    const getAddresses = useCallback(async (customerId) => {
        const values = await CustomerAPI.addresses(customerId);
        const addresses = _.sortBy(values, "contactName");
        setShipAddresses(addresses);
        if (_.size(values) > 0) {
            const val = values[0];
            form.setFieldValue("shipToId", val.id);
            form.setFieldValue("shipToContactName", val.contactName);
            form.setFieldValue("shipToAddress", val.address);
            form.setFieldValue("shipToCity", val.city);
            form.setFieldValue("shipToState", val.state);
            form.setFieldValue("shipToZip", val.zip);
        }
    }, [form]);

    const afterCustomerSelected = async (val) => {
        const customer = await CustomerAPI.get(val);
        form.setFieldValue("term", customer.term);
        getAddresses(val);

        // Check for past due invoices
        try {
            const pastDueAmount = await InvoiceAPI.getTotalPastDue({ customerId: val });
            if (pastDueAmount && pastDueAmount > 0) {
                await new Promise((resolve) => {
                    Modal.confirm({
                        title: 'Warning',
                        content: 'This customer has past due invoice. Make sure to get authorization from your manager before proceed!',
                        okText: 'Confirm',
                        hideCancel: true,
                        onOk: () => resolve(true),
                    });
                });
            }
        } catch (error) {
            console.error("Error checking past due invoices:", error);
        }
    }

    const calcQuantity = (alternativeQty, index) => {
        const field = `items[${index}]`;
        const itemId = form.getFieldValue(`${field}.itemId`);
        const item = _.find(items, item => item.id === itemId);
        if (alternativeQty && item?.additionalUomThreshold) {
            const qty = Decimal.mul(item.additionalUomThreshold, alternativeQty).toFixed(2);
            form.setFieldValue(`${field}.quantity`, qty);
        }
    }

    const calcAlternativeQuantity = (quantity, index) => {
        const field = `items[${index}]`;
        const itemId = form.getFieldValue(`${field}.itemId`);
        const item = _.find(items, item => item.id === itemId);
        if (quantity && item?.additionalUomThreshold) {
            const qty = Decimal.div(quantity, item.additionalUomThreshold).toFixed(2);
            form.setFieldValue(`${field}.alternativeQty`, qty);
        }
    }

    const calcAmount = (index) => {
        const field = "items[" + index + "]";
        const quantity = form.getFieldValue(field + ".quantity");
        const rate = form.getFieldValue(field + ".rate");
        if (quantity && rate) {
            const amount = Decimal.mul(quantity, rate).toNumber();
            form.setFieldValue(field + ".amount", amount);
        }
        calcTotal();
    }

    const calcTotal = () => {
        let formItems = form.getFieldValue("items");
        let total = new Decimal(0);
        for (const item of formItems) {
            total = Decimal.add(total, item.amount ?? 0);
        }
        form.setFieldValue("totalAmount", total.toNumber());
    }

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            setLoading(true);
            const id = await SOAPI.add(values);
            Message.success("Sale Order created successfully");
            navigate(`/workspace/sales/so/${id}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                if (error.status === "909001") {
                    Message.warning(error.message);
                } else {
                    Message.error(error.message || "Failed to create Sale Order");
                }
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Card title="Create New SO" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Submit</Button>
                <Button onClick={() => navigate("/workspace/sales/so")}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem>
                    <Row gutter={8}>
                        <Col span={8}>
                            <FormItem label="Customer" field="customerId"
                                labelCol={{ span: 8 }}
                                required rules={[{ required: true }]}>
                                <CustomerSelect onChange={val => afterCustomerSelected(val)} />
                            </FormItem>
                            <FormItem label="Ship To" field="shipToContactName" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                                required rules={[{ required: true }]}>
                                <Address.Select addresses={shipAddresses} onAddrChange={val => {
                                    form.setFieldValue("shipToContactName", val.contactName);
                                    form.setFieldValue("shipToAddress", val.address);
                                    form.setFieldValue("shipToCity", val.city);
                                    form.setFieldValue("shipToState", val.state);
                                    form.setFieldValue("shipToZip", val.zip);
                                }} />
                            </FormItem>
                            <FormItem field="shipToAddress" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToCity" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToState" hidden>
                                <Input />
                            </FormItem>
                            <FormItem field="shipToZip" hidden>
                                <Input />
                            </FormItem>
                            <FormItem shouldUpdate noStyle>
                                {(value) => {
                                    return (
                                        <FormItem wrapperCol={{ offset: 8, span: 16 }}>
                                            <Address value={{
                                                name: value.shipToContactName,
                                                address: value.shipToAddress,
                                                city: value.shipToCity,
                                                state: value.shipToState,
                                                zip: value.shipToZip,
                                            }} />
                                        </FormItem>
                                    )
                                }}
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="SO Date" field="soDate" labelCol={{ span: 8 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                            <FormItem label="Due Date" field="dueDate" labelCol={{ span: 8 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                            <FormItem label="Ready" field="readyDate" labelCol={{ span: 8 }} required rules={[{ required: true }]}>
                                <DatePicker />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Term" field="term" labelCol={{ span: 6 }}>
                                <EnumSelect enumObj={TermsDef} />
                            </FormItem>
                            <FormItem label="SO Total" field="totalAmount" labelCol={{ span: 6 }}>
                                <InputCurrency readOnly />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <FormItem label="Reference" labelCol={{ span: 2 }}>
                                <Form.List field="reference">
                                    {(fields, { add, remove }) =>
                                        <>
                                            {fields.map((item, index) => {
                                                return (
                                                    <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                        <Space className="form-item-row">
                                                            <FormItem field={item.field + ".type"}>
                                                                <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} showSearch />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".number"}>
                                                                <Input />
                                                            </FormItem>
                                                            <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                        </Space>
                                                    </FormItem>
                                                )
                                            })}
                                            <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                        </>
                                    }
                                </Form.List>
                            </FormItem>
                            <FormItem label="Memo" labelCol={{ span: 2 }} field="memo">
                                <TextArea />
                            </FormItem>
                        </Col>
                    </Row>
                </FormItem>
                <FormItem label="Items" labelCol={{ span: 2 }}>
                    <Form.List field="items">
                        {(fields, { add, remove }) =>
                            <>
                                <table width="100%">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th width="120">Item Number</th>
                                            <th>Item Name</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Alternative Qty</th>
                                            <th>U/M</th>
                                            <th>Rate</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <span>#{index + 1}</span>
                                                        <FormItem field={item.field + ".id"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".itemNo"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".itemId"} required rules={[{ required: true }]} noStyle>
                                                            <Select options={_.map(items, (item) => ({ value: item.id, label: item.itemName }))}
                                                                style={{ minWidth: 120 }}
                                                                showSearch
                                                                onChange={async (val) => {
                                                                    const value = _.find(items, item => item.id === val);
                                                                    let rate = value.salePrice;
                                                                    const customerId = form.getFieldValue("customerId");
                                                                    if (customerId) {
                                                                        const soItem = await SOAPI.getCustomerLastSOItem(customerId, val);
                                                                        if (soItem) {
                                                                            rate = soItem.rate;
                                                                        }
                                                                    }
                                                                    form.setFieldValue(item.field + ".itemNo", value.itemNo);
                                                                    form.setFieldValue(item.field + ".itemName", value.itemName);
                                                                    form.setFieldValue(item.field + ".description", value.description);
                                                                    form.setFieldValue(item.field + ".uom", value.uom);
                                                                    form.setFieldValue(item.field + ".rate", rate);
                                                                    form.setFieldValue(item.field + ".additionalUom", value.additionalUom);
                                                                    calcAmount(index);
                                                                }} />
                                                        </FormItem>
                                                        <FormItem field={item.field + ".itemName"} hidden>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".description"} noStyle>
                                                            <Input />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".quantity"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber onChange={(val) => {
                                                                calcAlternativeQuantity(val, index);
                                                                calcAmount(index);
                                                            }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".alternativeQty"} noStyle shouldUpdate>
                                                            {(values) => {
                                                                return (
                                                                    <InputNumber suffix={values.items[index]?.additionalUom ? `${values.items[index]?.additionalUom}` : ""}
                                                                        onChange={(val) => {
                                                                            calcQuantity(val, index);
                                                                            calcAmount(index);
                                                                        }} />
                                                                )
                                                            }}
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".uom"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".rate"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber onChange={() => calcAmount(index)} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".amount"} required rules={[{ required: true }]} noStyle>
                                                            <InputNumber readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem noStyle>
                                                            <Button type="text" status="danger" icon={<IconDelete />}
                                                                onClick={() => {
                                                                    remove(index);
                                                                    calcTotal();
                                                                }}></Button>
                                                        </FormItem>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                                <FormItem>
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Item</Button>
                                </FormItem>
                            </>
                        }
                    </Form.List>
                </FormItem>
            </Form>
        </Card>
    );
}
